<template>
    <filters-wrapper :has-active-filters="props.hasActiveFilters" @submit="emit('submit')" @reset="emit('reset')">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.active" @update:model-value="emit('submit')" />
        </template>
        <v-form class="table-filters">
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.type"
                        :items="getCompanyTypeItems()"
                        :label="$t('type')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="company-type"
                    />
                </v-col>
                <v-col v-if="!isSupplierPage" cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.contractGroupId"
                        :items="contractGroups"
                        item-title="name"
                        item-value="id"
                        :label="$t('group_of_contracts')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="contract-group-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.rangeType"
                        :items="getCompaniesDateRangeTypes()"
                        :label="$t('date')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="date-range-type"
                        :disabled="isSupplierPage"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <daterange
                        :start-date-label="$t('date_from')"
                        :end-date-label="$t('date_to')"
                        :dense="true"
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.rangeFrom"
                        :_end-date="filters.rangeTo"
                        data-cy="date-range"
                        :clearable="true"
                        @change-start-date="filters.rangeFrom = $event"
                        @change-end-date="filters.rangeTo = $event"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.countryId"
                        :items="useCountries"
                        :label="$t('country')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="country-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="company-name"
                    />
                </v-col>
                <v-col cols="12" :md="isSupplierPage ? 3 : 6">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="filters.reservationNumberFrom"
                                :label="`${$t('number_of_reservations')} (${$t('from_number')})`"
                                type="number"
                                hide-spin-buttons
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="reservation-number-from"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="filters.reservationNumberTo"
                                :label="`${$t('number_of_reservations')} (${$t('to_number')})`"
                                type="number"
                                hide-spin-buttons
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="reservation-number-to"
                            />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.cityId"
                        :items="cities"
                        :label="$t('city')"
                        :disabled="isCitiesLoading || !cities.length"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="city-id"
                    />
                </v-col>
                <v-col v-if="!isSupplierPage" cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.managerId"
                        item-title="name"
                        item-value="personId"
                        :items="users"
                        :label="$t('manager')"
                        :loading="loading"
                        :disabled="loading"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="manager-id"
                    />
                </v-col>
                <v-col v-if="!isSupplierPage" cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.organizationBalanceFrom"
                        :label="`${$t('current_balance')} (${$t('from_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="organization-balance-from"
                    />
                </v-col>
                <v-col v-if="!isSupplierPage" cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.organizationBalanceTo"
                        :label="`${$t('current_balance')} (${$t('to_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="organization-balance-to"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6" order="1" order-md="1">
                    <v-text-field
                        v-model="filters.companyCodeOrAlias"
                        :label="$t('company_code_or_alias')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="alias"
                    />
                </v-col>
                <v-col v-if="useIsShowField" cols="12" md="3" sm="6" order="3" order-md="2">
                    <v-select
                        v-model="filters.stage"
                        :items="useStages"
                        :label="$t('stage')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="stage"
                    />
                </v-col>
                <v-col v-if="useIsShowField" cols="12" md="3" sm="6" order="4" order-md="3">
                    <v-select
                        v-model="filters.industry"
                        :items="useIndustries"
                        :label="$t('industry')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="industry"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="0" class="px-0 py-0" order="5" order-md="4" />
                <v-col cols="12" md="3" sm="6" order="2" order-md="5">
                    <v-text-field
                        v-model="filters.phone"
                        :label="$t('phone')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="phone"
                    />
                </v-col>
                <v-col v-if="useIsShowField" cols="12" md="3" sm="6" order="5" order-md="6">
                    <v-select
                        v-model="filters.priorityLevel"
                        :items="usePriorities"
                        :label="$t('priority')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="priority"
                    />
                </v-col>
                <v-col v-if="useIsShowField" cols="12" md="3" sm="6" order="6" order-md="7">
                    <v-select
                        v-model="filters.companySize"
                        :items="useCompanySize"
                        :label="$t('company_size')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="company-size"
                    />
                </v-col>
            </v-row>
        </v-form>
        <template v-slot:actions>
            <v-col cols="12" md="6">
                <v-row no-gutters>
                    <custom-filter-modal
                        v-if="hasActiveFilters"
                        :filters="filters"
                        :person="person"
                        :mongo-collection="'companies_custom_filters'"
                        :company-type="companyType"
                        @create-custom-filter="updateQuickFilters"
                    />
                    <QuickFilter
                        ref="quickFilter"
                        :title="$t('saved_searches')"
                        :path="'companiesCustomFilters'"
                        :init-default-filters="initCompanyFilters"
                        :company-type="companyType"
                        @change="changeQuickFilter"
                    />
                </v-row>
            </v-col>
        </template>
    </filters-wrapper>
</template>

<script setup lang="ts">
    import type {CompanyFiltersType, ContractGroupsType} from '~/utils/types/account/companies/types'
    import {initCompanyFilters} from '~/utils/types/account/companies/helpers'
    import {Company, DataRange, type SelectOptionType} from '~/utils/types/types'
    import Daterange from '~/components/search/forms/Daterange.vue'
    import {useCompanySize, useIndustries, usePriorities, useStages, useCountries} from '~/composables/form-helpers'
    import CustomFilterModal from '~/components/account/filters/CustomFilterModal.vue'
    import QuickFilter from '~/components/account/filters/QuickFilter.vue'

    const props = defineProps<{
        hasActiveFilters: boolean
        companyType: string[]
    }>()
    const filters = defineModel<CompanyFiltersType>({
        default: initCompanyFilters(),
    })
    const emit = defineEmits<{
        (e: 'submit' | 'reset'): void
    }>()

    const loading = ref<boolean>(false)
    const users = ref<any[]>([])
    const contractGroups = ref<ContractGroupsType[]>([])
    const isCitiesLoading = ref<boolean>(true)
    const cities = ref<SelectOptionType[]>([])
    const person = ref(authStore.person)
    const isSupplierPage = ref(props.companyType.includes(Company.HubSupplier || Company.InternalSupplier))

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(
        () => filters.value.countryId,
        async (): Promise<void> => {
            filters.value.cityId = null
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            filters.value.countryId ? await loadCities() : (cities.value = [])
        },
        {deep: true}
    )

    async function loadCities(): Promise<void> {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: filters.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    onMounted(async () => {
        try {
            loading.value = true
            users.value = (await app.$api.persons.get({companyId: authStore.company.id})).users
                .map(({personId, firstName, lastName}) => ({
                    personId,
                    name: `${lastName || ''} ${firstName || ''}`.trim(),
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
            contractGroups.value = (await app.$api.contractGroups.get()).contractGroups
        } catch (e) {
            users.value = []
        } finally {
            loading.value = false
        }
    })

    function getCompanyTypeItems(): SelectOptionType[] {
        const companyTypes = props.companyType
        if (Array.isArray(companyTypes)) {
            return companyTypes.map(value => ({
                value,
                title: t(`companyTypes.${value}`),
            }))
        } else {
            return []
        }
    }

    const getCompaniesDateRangeTypes = (): SelectOptionType[] => {
        let dateTypes: DataRange[] = [
            DataRange.Registration,
            DataRange.FirstReservation,
            DataRange.LastReservation,
            DataRange.Expiration,
            DataRange.ContractExpiration,
            DataRange.dateOfBirth,
        ]
        if (isSupplierPage.value) {
            filters.value.rangeType = DataRange.Registration
            dateTypes = [DataRange.Registration]
        }
        return dateTypes.map(value => ({value, title: t(`date_types.${value}`)}))
    }

    const changeQuickFilter = (value: CompanyFiltersType): void => {
        filters.value = value
        emit('submit')
    }

    const quickFilter = ref(null)
    const updateQuickFilters = (): void => {
        quickFilter.value.loadCustomFilters()
    }

    const isAgency = computed(() => authStore.isAgency)

    const isTO2 = computed(() => authStore.isTO2)

    const isCRMExtendedModule = computed(() => runtimeStore.CRMSettings?.extendedCrm || false)

    const useIsShowField = computed(() => isCRMExtendedModule.value && !isAgency.value && !isTO2.value)
</script>
